import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { pickLocalized } from "@/lib/i18n";
import useSiteContent from "@/lib/useSiteContent";
import { platformMediaSrc } from "../../lib/platformMedia";
import { formatPrice, OPERATING_CURRENCIES } from "./_shared";
import { annualAmount, defaultFunnelFacts, maxAnnualSavingPercent, monthlyAmount } from "./pricingFacts";

/**
 * Adapts the wire shape's { <field>, <field>_i18n: {fr, es} } pair (feature_rows' `label`
 * / `label_i18n` — contract §3.3) to the flat "_en"/"_fr"/"_es" suffix shape
 * `pickLocalized` (frontend/src/i18n/pickLocalized.js) already knows how to resolve.
 * The canonical value is written under the "_en" suffix explicitly so an "en" reader's
 * chain (["_en","_fr"]) resolves to it directly rather than falling through to a French
 * override that happens to be present; a missing fr/es overlay degrades to this same
 * canonical EN, matching the project's EN-default law.
 */
function localize(canonical, i18nOverlay, field, lang) {
  return pickLocalized(
    { [`${field}_en`]: canonical, [`${field}_fr`]: i18nOverlay?.fr, [`${field}_es`]: i18nOverlay?.es },
    field,
    lang,
  );
}

/**
 * Currency selector + billing toggle (extracted to limit re-renders).
 *
 * The annual option's label used to read "Annual · 2 months free" — an English literal on
 * a trilingual page, stating a fact ("2 months free") that is only the DEFAULT applied
 * when a plan carries no explicit annual price (backend/saas_models.py:139). A plan priced
 * annually by hand in super admin discounts by whatever the operator typed, and the toggle
 * went on promising two free months regardless. It now states the saving actually derivable
 * from the prices on the cards below it, and says nothing when none is.
 */
function PricingToggles({ currency, setCurrency, billing, setBilling, savingPercent }) {
  const { text } = useSiteContent();
  return (
    <div className="flex items-center justify-center gap-6 mb-14 flex-wrap">
      <div className="flex gap-1 p-1 rounded-full bg-white border border-sol-midnight/10" data-testid="currency-selector">
        {OPERATING_CURRENCIES.map((c) => (
          <button
            key={c}
            onClick={() => setCurrency(c)}
            data-testid={`currency-${c.toUpperCase()}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
              currency === c ? "bg-sol-midnight text-sol-ivory" : "text-sol-midnight/60 hover:text-sol-midnight"
            }`}
          >
            {c.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="flex gap-1 p-1 rounded-full bg-white border border-sol-midnight/10" data-testid="billing-selector">
        {[
          { key: "monthly", label: text("pricing.monthly") },
          {
            key: "annual",
            label: savingPercent !== null
              ? text("pricing.annualWithSave", { percent: savingPercent })
              : text("pricing.annual"),
          },
        ].map((b) => (
          <button
            key={b.key}
            onClick={() => setBilling(b.key)}
            data-testid={`billing-${b.key}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
              billing === b.key ? "bg-sol-gold text-sol-midnight" : "text-sol-midnight/60 hover:text-sol-midnight"
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * One plan card (keeps the parent simple).
 */
function PlanCard({ plan, currency, billing }) {
  const navigate = useNavigate();
  // `t` only for the plan tagline override: `pricing.tier.*` is catalogue copy owned by the
  // Plans manager, not site copy (contract §2.8).
  const t = useT("saas");
  const { text } = useSiteContent();
  const { lang } = useLanguage();
  const featured = plan.plan_id === "flow";
  const isOrbit = plan.plan_id === "orbit";
  const tierTagline = t(`pricing.tier.${plan.plan_id}`);
  const tagline = tierTagline && !tierTagline.startsWith("pricing.tier.") ? tierTagline : plan.tagline;
  // The "× 10, NEVER × 12" fallback now lives in `pricingFacts.annualAmount`, alongside
  // the page's other price derivations: the FAQ states the annual saving in prose, and two
  // separate formulas for one figure are exactly the drift BUG-205 reports. Both readers
  // call the same function.
  const monthly = monthlyAmount(plan, currency) || 0;
  const annual = annualAmount(plan, currency) || 0;
  const shown = billing === "annual" ? annual / 12 : monthly;

  return (
    <div
      data-testid={`plan-card-${plan.plan_id}`}
      className={`relative rounded-3xl p-8 transition-all duration-500 flex flex-col ${
        featured
          ? "bg-sol-midnight text-sol-ivory shadow-2xl shadow-sol-midnight/20 lg:scale-[1.04]"
          : "bg-white border border-sol-midnight/8 hover:shadow-xl"
      }`}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-sol-gold text-sol-midnight text-xs font-semibold rounded-full">
          {text("landing.pkg.mostPopular")}
        </div>
      )}
      {plan.image_url && (
        <img
          src={platformMediaSrc(plan.image_url)}
          alt=""
          data-testid={`plan-image-${plan.plan_id}`}
          className="w-full h-32 object-cover rounded-2xl mb-5"
        />
      )}
      <h3 className={`font-sol font-semibold text-2xl mb-1 ${featured ? "text-sol-ivory" : "text-sol-midnight"}`}>
        {plan.name}
      </h3>
      <p className={`text-sm mb-5 leading-snug ${featured ? "text-sol-ivory/70" : "text-sol-midnight/60"}`}>{tagline}</p>
      <div className="mb-3 flex items-baseline gap-1.5">
        {plan.is_custom ? (
          <span className={`font-sol font-semibold text-3xl ${featured ? "text-sol-ivory" : "text-sol-midnight"}`}>
            {text("landing.pkg.fromPrice", { price: formatPrice(monthly, currency) })}
          </span>
        ) : (
          <>
            <span className={`font-sol font-semibold text-5xl tracking-tight ${featured ? "text-sol-ivory" : "text-sol-midnight"}`}>
              {formatPrice(shown, currency)}
            </span>
            <span className={`text-sm ${featured ? "text-sol-ivory/50" : "text-sol-midnight/40"}`}>{text("pricing.perMonth")}</span>
          </>
        )}
      </div>
      {billing === "annual" && !plan.is_custom && (
        <p className="text-xs mb-4 text-sol-gold">
          {text("pricing.billedAnnually", {
            total: formatPrice(annual, currency),
            saved: formatPrice(monthly * 12 - annual, currency),
          })}
        </p>
      )}
      {plan.extra_practitioner_price && (
        <p className={`text-xs mb-4 ${featured ? "text-sol-ivory/55" : "text-sol-midnight/50"}`}>
          {text("pricing.extraPractitioner", {
            price: formatPrice(plan.extra_practitioner_price[currency], currency),
          })}
        </p>
      )}
      <ul className="space-y-2.5 mb-8 text-sm flex-1">
        {(plan.feature_rows || []).map((row) => {
          const label = localize(row.label, row.label_i18n, "label", lang);
          const isComingSoon = row.status === "coming_soon";
          const isUnlimited = row.kind === "limit" && row.value === -1;
          // A limit row's SENTENCE goes through the locale layer too, not just its label:
          // `label_template_i18n` is resolved by the same chain as `label_i18n`, so a
          // French reader gets "Jusqu'à 5 praticiens" rather than a French label followed
          // by an English line carrying the actual number.
          const template = localize(row.label_template, row.label_template_i18n, "tpl", lang);
          const displayLabel = row.kind === "limit"
            ? (isUnlimited
                // Deliberately NOT substituted into the template: "Up to Unlimited
                // practitioners" is broken grammar in every locale, so the unlimited case
                // reads as its own sentence built from the plain label.
                ? `${label} — ${text("pricing.unlimited")}`
                : (template || label).replace(/\{value\}/g, String(row.value)))
            : label;
          return (
            <li
              key={`pf-${plan.plan_id}-${row.key}`}
              data-testid={`plan-feature-${plan.plan_id}-${row.key}`}
              className={`flex gap-2.5 ${featured ? "text-sol-ivory/80" : "text-sol-midnight/75"}`}
            >
              {isComingSoon
                ? <Clock className={`w-4 h-4 flex-shrink-0 mt-0.5 ${featured ? "text-sol-ivory/40" : "text-sol-midnight/30"}`} />
                : <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${featured ? "text-sol-gold" : "text-sol-sage"}`} />}
              <span className={isComingSoon ? (featured ? "text-sol-ivory/50" : "text-sol-midnight/45") : undefined}>
                {displayLabel}
                {/* inline-block + whitespace-nowrap: as a plain inline span inside the
                    label's text flow, the pill line-wrapped mid-chip on these narrow cards
                    — the rounded background split across two lines and the "·" separator
                    dangled at the end of the first ("Coming soon ·" / "Q2 2026"). Seen on
                    four rows across the Flow/Ascend/Orbit cards in browser QA. */}
                {isComingSoon && (
                  <span
                    data-testid={`plan-feature-soon-${plan.plan_id}-${row.key}`}
                    className={`ml-1.5 inline-block whitespace-nowrap align-baseline text-[11px] px-1.5 py-0.5 rounded-full ${featured ? "bg-sol-ivory/10 text-sol-ivory/70" : "bg-sol-midnight/5 text-sol-midnight/50"}`}
                  >
                    {text("pricing.comingSoon")}{row.eta ? ` ${text("pricing.eta", { eta: row.eta })}` : ""}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <Button
        data-testid={`select-plan-${plan.plan_id}`}
        onClick={() =>
          isOrbit
            ? (window.location.href = "mailto:hey@solunary.health")
            : navigate(`/platform/signup?plan=${plan.plan_id}&currency=${currency}&billing=${billing}`)
        }
        className={`w-full rounded-2xl py-6 font-semibold ${
          featured
            ? "bg-sol-gold hover:bg-sol-gold/90 text-sol-midnight"
            : "bg-sol-midnight hover:bg-sol-midnight/90 text-sol-ivory"
        }`}
      >
        {isOrbit ? text("pricing.contactSales") : text("pricing.choosePlan", { plan: plan.name })}
      </Button>
    </div>
  );
}

// A plan with no positive monthly price in the selected currency is NOT sold in that
// currency (super_admin_routes.py `_require_a_sellable_price`: at least one positive
// currency, never all of them) — signup refuses it with 400 "Plan not available in
// {currency}" (saas_routes.py:213-215). Showing its card at $0 with a live CTA would
// promise a purchase that fails on the first click, so the card is hidden instead.
const isSoldIn = (plan, currency) => Number(plan.prices?.[currency]) > 0;

/**
 * `id="pricing"` IS A CONTRACT: it is one of the registry's ANCHORS
 * (backend/modules/site_content/registry.py), targeted by the seeded header and footer
 * Pricing links; the backend drift test asserts every anchor is rendered as an id.
 *
 * `skus` IS NEW and it is here for the default-funnel pitch below, which quotes the total
 * of two SKU rows. `PlatformLanding` already fetched them for `TurnkeyAndPorting`.
 */
export default function PricingPlans({ plans, skus = [], currency, setCurrency, billing, setBilling }) {
  const { text } = useSiteContent();
  const visiblePlans = plans.filter((plan) => isSoldIn(plan, currency));
  const savingPercent = maxAnnualSavingPercent(plans, currency);
  const funnel = defaultFunnelFacts(plans, skus, currency);
  return (
    <section id="pricing" className="py-28 lg:py-36 bg-gradient-to-b from-sol-ivory to-amber-50/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-8">
          <div className="text-xs uppercase tracking-[0.24em] text-sol-gold mb-4">{text("pricing.eyebrow")}</div>
          <h2 className="font-display text-4xl lg:text-5xl text-sol-midnight leading-tight">
            {text("pricing.headingA")} <span className="italic">{text("pricing.headingB")}</span>.
          </h2>
          {/* BUG-205: this read "Pick monthly or save ~17% with annual billing." — the
              same 17 the FAQ stated, in a JSX literal, in English, on a trilingual page.
              Neither copy read the plan prices rendered directly below them. */}
          <p className="mt-6 text-sol-midnight/60" data-testid="pricing-annual-saving">
            {savingPercent !== null
              ? text("pricing.pickCycleSave", { percent: savingPercent })
              : text("pricing.pickCycle")}
          </p>
        </div>

        {/* Both-pillars banner */}
        <div data-testid="pricing-both-pillars" className="max-w-3xl mx-auto mb-12 rounded-2xl bg-white border border-sol-gold/30 px-6 py-4 text-center text-sm text-sol-midnight/80">
          {text("pricing.bothPillars")}
        </div>

        <PricingToggles
          currency={currency}
          setCurrency={setCurrency}
          billing={billing}
          setBilling={setBilling}
          savingPercent={savingPercent}
        />

        <div className="grid lg:grid-cols-4 gap-5">
          {visiblePlans.map((plan) => (
            <PlanCard key={plan.plan_id} plan={plan} currency={currency} billing={billing} />
          ))}
        </div>

        {/* THE DEFAULT FUNNEL PITCH — absent, never partial.
            It read `"Flow + Turnkey Install + Express Port" = $129/mo + $1,398 one-time`:
            a third copy of the Flow price and a HAND-COMPUTED sum of two SKU prices
            (899 + 499), as an English JSX literal. Both amounts and all three names now
            come from the catalogue (`pricingFacts.defaultFunnelFacts`), and the whole block
            disappears when any of the three rows it names is not on sale — a bundle whose
            parts cannot all be bought has no honest total to quote. */}
        {funnel && (
          <div className="mt-16 bg-sol-midnight rounded-3xl p-10 lg:p-12 text-center text-sol-ivory max-w-4xl mx-auto" data-testid="funnel-pitch">
            <div className="text-xs uppercase tracking-[0.24em] text-sol-gold mb-3">{text("pricing.funnel.eyebrow")}</div>
            <p className="text-2xl lg:text-3xl font-display leading-snug">
              {text("pricing.funnel.combo", { plan: funnel.plan, install: funnel.install, porting: funnel.porting })}
              <br />
              <span className="text-sol-gold">
                {text("pricing.funnel.amount", { monthly: funnel.monthly, oneTime: funnel.oneTime })}
              </span>{" "}
              {text("pricing.funnel.promise")}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
